import clsx from 'clsx';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  arrow,
} from '@floating-ui/react';
import { useRef, useState, type ReactElement, type ReactNode } from 'react';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content?: ReactNode;
  placement?: Placement;
  className?: string;
  children: ReactElement;
  /** Render tooltip in a portal to escape overflow:hidden containers */
  portal?: boolean;
}

const TOOLTIP_CLASSES =
  'w-max max-w-xs rounded bg-foreground px-2 py-1 text-xs/5 text-background shadow-md';

// ============================================================================
// CSS-only tooltip (default) -- zero JS on hover, pure CSS group-hover
// ============================================================================

const cssArrowStyles = {
  top: 'before:top-full before:-mt-px before:left-1/2 before:-translate-x-1/2 before:border-t-foreground before:border-x-transparent before:border-b-transparent',
  bottom:
    'before:bottom-full before:-mb-px before:left-1/2 before:-translate-x-1/2 before:border-b-foreground before:border-x-transparent before:border-t-transparent',
  left: 'before:left-full before:-ml-px before:top-1/2 before:-translate-y-1/2 before:border-l-foreground before:border-y-transparent before:border-r-transparent',
  right:
    'before:right-full before:-mr-px before:top-1/2 before:-translate-y-1/2 before:border-r-foreground before:border-y-transparent before:border-l-transparent',
};

function CSSTooltip({
  content,
  placement = 'top',
  className,
  children,
}: Omit<TooltipProps, 'portal'>) {
  return (
    <span className="group/tip relative inline-flex">
      {children}
      <span
        role="tooltip"
        className={clsx(
          'pointer-events-none absolute z-50 opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150',
          TOOLTIP_CLASSES,
          'before:absolute before:border-4 before:border-solid',
          cssArrowStyles[placement!],
          placement === 'top' && 'bottom-full left-1/2 -translate-x-1/2 mb-2',
          placement === 'bottom' && 'top-full left-1/2 -translate-x-1/2 mt-2',
          placement === 'left' && 'right-full top-1/2 -translate-y-1/2 mr-2',
          placement === 'right' && 'left-full top-1/2 -translate-y-1/2 ml-2',
          className
        )}
      >
        {content}
      </span>
    </span>
  );
}

// ============================================================================
// Portal tooltip -- uses @floating-ui/react for overflow-safe positioning
// ============================================================================

function PortalTooltip({
  content,
  placement = 'top',
  className,
  children,
}: Omit<TooltipProps, 'portal'>) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { move: false }),
    useFocus(context),
    useDismiss(context),
    useRole(context, { role: 'tooltip' }),
  ]);

  const arrowX = middlewareData.arrow?.x;
  const arrowY = middlewareData.arrow?.y;
  const side = (context.placement ?? placement).split('-')[0] as
    | 'top'
    | 'bottom'
    | 'left'
    | 'right';
  const arrowSide = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[side];

  return (
    <>
      <span ref={refs.setReference} className="inline-flex" {...getReferenceProps()}>
        {children}
      </span>
      <FloatingPortal id="tooltip-portal">
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx('z-50 pointer-events-none', TOOLTIP_CLASSES, className)}
            {...getFloatingProps()}
          >
            {content}
            <div
              ref={arrowRef}
              className="absolute h-2.5 w-2.5 rotate-45 bg-foreground"
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                [arrowSide]: '-5px',
              }}
            />
          </div>
        )}
      </FloatingPortal>
    </>
  );
}

// ============================================================================
// Public API
// ============================================================================

export function Tooltip({ content, portal, ...rest }: TooltipProps) {
  if (!content) return rest.children;
  const props = { content, ...rest };
  return portal ? <PortalTooltip {...props} /> : <CSSTooltip {...props} />;
}
